import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { TableSkeleton } from '../components/ui/Skeletons'
import { StatusBadge } from '../components/ui/StatusBadge'
import { EmptyState } from '../components/ui/EmptyState'
import { GlowCard } from '../components/ui/GlowCard'
import { Button } from '../components/ui/Button'
import { SiteCreateModal } from '../components/site/SiteCreateModal'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface SiteListItem {
  id: string
  slug: string
  name: string
  client_id: string
  client_name: string | null
  address: string | null
  city: string | null
  state: string | null
  status: string
  device_count: number
  updated_at: string
}

export default function Sites() {
  const [sites, setSites] = useState<SiteListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const navigate = useNavigate()

  const loadSites = useCallback(async () => {
    try {
      const res = await api.get<{ data: SiteListItem[] }>(`/sites?limit=100&search=${encodeURIComponent(search)}`)
      setSites(res.data)
    } catch { setSites([]) }
    finally { setLoading(false) }
  }, [search])

  useEffect(() => { loadSites() }, [loadSites])

  if (loading) return <TableSkeleton cols={6} />

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title">Sites</h1>
        <Button icon={PlusIcon} onClick={() => setShowCreate(true)}>New Site</Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          className="input"
          placeholder="Search sites..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360, fontSize: 13 }}
        />
      </div>

      {sites.length > 0 ? (
        <GlowCard style={{ padding: 0 }}>
          <table className="v3-table">
            <thead>
              <tr>
                {['Site Name', 'Client', 'Address', 'Devices', 'Status', 'Last Updated'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sites.map(site => (
                <tr
                  key={site.id}
                  onClick={() => navigate(`/sites/${site.slug}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span style={{
                      fontSize: 13, fontWeight: 500, color: 'var(--accent)',
                      transition: 'color var(--dur-f)',
                    }}>
                      {site.name}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-2)' }}>
                    {site.client_name || '\u2014'}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-2)' }}>
                    {[site.address, site.city, site.state].filter(Boolean).join(', ') || '\u2014'}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
                    {site.device_count}
                  </td>
                  <td>
                    <StatusBadge status={site.status} />
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-3)' }}>
                    {new Date(site.updated_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlowCard>
      ) : (
        <EmptyState
          title="No sites yet — add your first location"
          description="Create a new site to start managing locations."
          action={<Button icon={PlusIcon} onClick={() => setShowCreate(true)}>New Site</Button>}
        />
      )}

      <SiteCreateModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(site) => {
          setShowCreate(false)
          navigate(`/sites/${site.slug}`)
        }}
      />
    </div>
  )
}
